import React, { useState, useEffect } from 'react';
import { useProfile } from '../context/ProfileContext';
import Header from '../header/AppHeader';
import ProfileCard from '../profile/ProfileCard';
import background from "../../assets/dashboard-bg.jpg";

const Leaderboard = () => {
  const { data: dashboardData, scoreboard, loadScoreboard } = useProfile();
  const [error, setError] = useState(null);

  // Loads once and is cached in context — no refetch when returning to this page.
  useEffect(() => {
    loadScoreboard().catch(() =>
      setError("Unable to sync leaderboard workspace info. Please try again.")
    );
  }, [loadScoreboard]);

  const leaderboardData = scoreboard || [];

  if (!scoreboard && !error) {
    return (
      <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex items-center justify-center font-montserrat">
        <div className="text-lg opacity-70 animate-pulse font-montserrat">Syncing dynamic rankings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex flex-col items-center justify-center p-4 font-montserrat">
        <p className="text-red-400 mb-4 font-medium font-montserrat">{error}</p>
        <button
          onClick={() => { setError(null); loadScoreboard(true).catch(() => setError("Unable to sync leaderboard workspace info. Please try again.")); }}
          className="bg-[#D15B28] text-white px-6 py-2 rounded-full font-semibold transition hover:bg-[#b04a1f] font-montserrat"
        >
          Retry
        </button>
      </div>
    );
  }

  const profile = dashboardData?.profile || {};

  return (
    <div
      className="h-[100dvh] w-full overflow-y-auto overflow-x-hidden lg:overflow-hidden bg-center bg-cover bg-no-repeat text-[#2B1B17] font-montserrat flex flex-col antialiased"
      style={{ backgroundImage: `url(${background})` }}
    >
      
      {/* Explicitly declaring this page instance as the active highlight */}
      <Header activeTab="Leaderboard" />

      {/* --- HERO SECTION --- */}
      <section className="px-6 md:px-12 pt-4 pb-3 max-w-[1600px] mx-auto w-full flex-shrink-0 font-montserrat">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 relative z-10">
          
          <div className="text-white flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 max-w-3xl font-montserrat">
            <h1 className="text-[44px] sm:text-[56px] md:text-[70px] font-barlow font-black tracking-tight drop-shadow-sm leading-none">
              Leaderboard
            </h1>
            <p className="text-sm md:text-base font-montserrat text-white/90 leading-relaxed font-normal drop-shadow-sm sm:max-w-[40rem]">
              See how you stack up against every Campus Ambassador across colleges, ranked live by total points.
            </p>
          </div>

          {/* PROFILE CARD */}
          <ProfileCard />

        </div>
      </section>

      {/* --- LEADERBOARD GRID PANEL --- */}
      <main className="max-w-[1600px] mx-auto px-2 md:px-2 w-full relative z-20 lg:flex-1 lg:min-h-0 pb-2 flex flex-col font-montserrat">
        <div className="bg-[#F5EFEB]/90 backdrop-blur-md rounded-2xl p-5 md:p-6 shadow-xl border border-white/20 flex flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden font-montserrat">
          
          <div className="flex justify-between items-center mb-3 pb-3 border-b border-black/5 flex-shrink-0 font-montserrat">
            <div>
              <h2 className="text-sm font-black tracking-widest text-[#2B1B17]/60 uppercase font-montserrat">LEADERBOARD</h2>
              <p className="text-xs text-black/40 font-medium mt-0.5 font-montserrat">Updated live</p>
            </div>
          </div>

          <div className="overflow-x-auto lg:overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-black/10 scrollbar-track-transparent lg:flex-1 lg:min-h-0">
            <table className="w-full text-left border-collapse font-montserrat">
              <thead className="sticky top-0 bg-[#F5EFEB] z-30 shadow-[0_1px_0_0_rgba(0,0,0,0.05)] font-montserrat">
                <tr className="text-[11px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase font-montserrat">
                  <th className="py-3 px-4 w-24 font-montserrat">Rank</th>
                  <th className="py-3 px-4 font-montserrat">Name</th>
                  <th className="py-3 px-4 font-montserrat">College</th>
                  <th className="py-3 px-4 text-right font-montserrat">Points</th>
                </tr>
              </thead>
              <tbody className="text-sm font-medium font-montserrat">
                {leaderboardData.map((entry, index) => {
                  const rank = index + 1;
                  // Match on id — names are not unique, so comparing them
                  // highlighted every CA who happened to share a name.
                  const isCurrentUser =
                    profile.id != null && entry.id === profile.id;

                  return (
                    <tr
                      key={entry.id ?? index}
                      className={`border-b border-black/[0.03] last:border-none transition font-montserrat ${
                        isCurrentUser ? "bg-[#D15B28]/10 text-[#2B1B17]" : "hover:bg-black/[0.01]"
                      }`}
                    >
                      <td className="py-3 px-4 font-black font-montserrat">
                        {rank === 1 && <span className="text-[#E65C00] font-black text-base font-montserrat">★ 1</span>}
                        {rank === 2 && <span className="text-[#2B1B17] opacity-70 font-black font-montserrat">2</span>}
                        {rank === 3 && <span className="text-[#2B1B17] opacity-50 font-black font-montserrat">3</span>}
                        {rank > 3 && <span className="text-black/40 font-bold font-montserrat">#{rank}</span>}
                      </td>

                      <td className="py-3 px-4 font-bold text-[#2B1B17] font-montserrat">
                        <div className="flex items-center gap-2 font-montserrat">
                          <span className="font-montserrat">{entry.name}</span>
                          {isCurrentUser && (
                            <span className="bg-[#D15B28] text-white text-[9px] font-extrabold tracking-widest px-1.5 py-0.5 rounded uppercase scale-90 font-montserrat">
                              YOU
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="py-3 px-4 text-black/60 font-medium font-montserrat">
                        {entry.college || "—"}
                      </td>

                      <td className="py-3 px-4 text-right font-black text-[#2B1B17] font-montserrat">
                        {Number(entry.points || 0).toLocaleString()} <span className="text-[11px] font-normal opacity-50 ml-0.5 font-montserrat">pts</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Leaderboard;